import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Icon from '../../components/AppIcon';
import RoleAccessSummary from '../../components/auth/RoleAccessSummary';
import { userService } from '../../services/userService';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS, ROLE_OPTIONS } from '../../constants/accessControl';
import { getRoleLabel } from '../../utils/permissions';
import { TANZANIA_REGION_OPTIONS, getRegionLabel } from '../../utils/tanzaniaRegions';

const roleOptions = ROLE_OPTIONS;

const initialFormState = {
  fullName: '',
  email: '',
  phone: '',
  role: '',
  region: '',
  extraPermissions: [],
  password: '',
  confirmPassword: '',
};

const initialFilters = {
  searchTerm: '',
  role: 'all',
};

const UserManagementPage = () => {
  const navigate = useNavigate();
  const { userProfile, loading: authLoading, hasPermission } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [usersError, setUsersError] = useState(null);
  const [formState, setFormState] = useState(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [banner, setBanner] = useState(null);
  const [filtersDraft, setFiltersDraft] = useState(initialFilters);
  const [activeFilters, setActiveFilters] = useState(initialFilters);
  const [updatingUserIds, setUpdatingUserIds] = useState(new Set());
  const [resettingUserIds, setResettingUserIds] = useState(new Set());
  const [refreshToken, setRefreshToken] = useState(0);
  const [editUserModalOpen, setEditUserModalOpen] = useState(false);
  const [userBeingEdited, setUserBeingEdited] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    fullName: '',
    phone: '',
    role: '',
    region: '',
    extraPermissions: [],
  });
  const [editUserErrors, setEditUserErrors] = useState({});
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [deleteUserTarget, setDeleteUserTarget] = useState(null);
  const [isDeletingUser, setIsDeletingUser] = useState(false);

  const canManageUsers = hasPermission(PERMISSIONS.ADMIN_USERS);

  useEffect(() => {
    if (authLoading) return;

    if (!canManageUsers) {
      navigate('/admin-dashboard', { replace: true });
    }
  }, [authLoading, canManageUsers, navigate]);

  const breadcrumbItems = useMemo(
    () => [
      { label: 'Admin Dashboard', path: '/admin-dashboard' },
      { label: 'User Management', path: '/user-management' },
    ],
    []
  );

  useEffect(() => {
    let isMounted = true;
    const loadUsers = async () => {
      setIsLoadingUsers(true);
      setUsersError(null);

      try {
        const data = await userService.listUsers({
          searchTerm: activeFilters?.searchTerm,
          role: activeFilters?.role,
        });

        if (isMounted) {
          setUsers(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (isMounted) {
          const message = error?.message || 'Failed to load users. Please try again.';
          setUsersError(message);
          setBanner({ type: 'error', message });
        }
      } finally {
        if (isMounted) {
          setIsLoadingUsers(false);
        }
      }
    };

    loadUsers();

    return () => {
      isMounted = false;
    };
  }, [activeFilters, refreshToken]);

  const handleSidebarToggle = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const handleFormChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }));
  };

  const validateForm = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formState?.fullName?.trim()) {
      errors.fullName = 'Full name is required.';
    }

    if (!formState?.email?.trim()) {
      errors.email = 'Email is required.';
    } else if (!emailRegex.test(formState?.email?.trim())) {
      errors.email = 'Enter a valid email address.';
    }

    if (!formState?.role) {
      errors.role = 'Select a role for the new user.';
    }

    if (formState?.role === 'region_admin' && !formState?.region) {
      errors.region = 'Select the region this admin will manage.';
    }

    if (!formState?.password) {
      errors.password = 'Password is required.';
    } else if (formState?.password?.length < 8) {
      errors.password = 'Password must be at least 8 characters.';
    }

    if (!formState?.confirmPassword) {
      errors.confirmPassword = 'Confirm the password.';
    } else if (formState?.confirmPassword !== formState?.password) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const resetForm = () => {
    setFormState(initialFormState);
    setFormErrors({});
  };

  const handleCreateUser = async (event) => {
    event?.preventDefault();
    setBanner(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const newUser = await userService.createUser({
        email: formState?.email?.trim(),
        password: formState?.password,
        fullName: formState?.fullName?.trim(),
        role: formState?.role,
        phone: formState?.phone?.trim() || null,
        region: formState?.role === 'region_admin' ? formState?.region : null,
        permissions: formState?.extraPermissions || [],
      });

      if (newUser) {
        setUsers((prev) => [newUser, ...prev]);
      } else {
        setRefreshToken((prev) => prev + 1);
      }

      setBanner({
        type: 'success',
        message: 'User account created successfully and confirmation email sent.',
      });
      resetForm();
    } catch (error) {
      const message =
        error?.message ||
        'Unable to create the user right now. Please verify your credentials and try again.';
      setBanner({ type: 'error', message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendPasswordReset = async (user) => {
    if (!user?.email) {
      setBanner({
        type: 'error',
        message: 'This user does not have an email address on file.',
      });
      return;
    }

    setBanner(null);
    setResettingUserIds((prev) => {
      const next = new Set(prev);
      next.add(user.id);
      return next;
    });

    try {
      await userService.sendPasswordResetEmail({
        email: user.email,
        fullName: user.fullName,
      });

      setBanner({
        type: 'success',
        message: `Password reset email sent to ${user.email}.`,
      });
    } catch (error) {
      setBanner({
        type: 'error',
        message: error?.message || 'Failed to send password reset email. Please try again.',
      });
    } finally {
      setResettingUserIds((prev) => {
        const next = new Set(prev);
        next.delete(user.id);
        return next;
      });
    }
  };

  const handleToggleUserStatus = async (user) => {
    if (!user?.id) return;

    setBanner(null);
    setUpdatingUserIds((prev) => {
      const next = new Set(prev);
      next.add(user?.id);
      return next;
    });

    try {
      const updated = await userService.toggleUserActive(user?.id, !user?.isActive);

      setUsers((prev) =>
        prev?.map((existing) => (existing?.id === user?.id ? updated : existing))
      );

      setBanner({
        type: 'success',
        message: `User ${updated?.isActive ? 'activated' : 'deactivated'} successfully.`,
      });
    } catch (error) {
      const message =
        error?.message || 'Failed to update the user status. Please try again later.';
      setBanner({ type: 'error', message });
    } finally {
      setUpdatingUserIds((prev) => {
        const next = new Set(prev);
        next.delete(user?.id);
        return next;
      });
    }
  };

  const openEditUserModal = (user) => {
    if (!user) return;
    setUserBeingEdited(user);
    setEditUserForm({
      fullName: user?.fullName || '',
      phone: user?.phone || '',
      role: user?.role || '',
      region: user?.region || '',
      extraPermissions: user?.permissions || [],
    });
    setEditUserErrors({});
    setEditUserModalOpen(true);
  };

  const closeEditUserModal = () => {
    setEditUserModalOpen(false);
    setUserBeingEdited(null);
    setEditUserErrors({});
  };

  const handleEditUserChange = (field, value) => {
    setEditUserForm((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (editUserErrors?.[field]) {
      setEditUserErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validateEditUserForm = () => {
    const errors = {};
    if (!editUserForm?.fullName?.trim()) {
      errors.fullName = 'Full name is required.';
    }
    if (!editUserForm?.role) {
      errors.role = 'Select a role.';
    }
    if (editUserForm?.role === 'region_admin' && !editUserForm?.region) {
      errors.region = 'Select the region this admin will manage.';
    }
    setEditUserErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleUpdateUserProfile = async (event) => {
    event?.preventDefault();
    if (!userBeingEdited?.id) return;
    if (!validateEditUserForm()) return;

    setIsSavingUser(true);
    setEditUserErrors({});

    try {
      const updatedUser = await userService.updateUserProfile(userBeingEdited.id, {
        fullName: editUserForm?.fullName?.trim(),
        phone: editUserForm?.phone?.trim() || null,
        role: editUserForm?.role,
        region: editUserForm?.role === 'region_admin' ? editUserForm?.region || null : null,
        permissions: editUserForm?.extraPermissions || [],
      });

      setUsers((prev) =>
        prev?.map((user) => (user?.id === updatedUser?.id ? updatedUser : user))
      );
      setBanner({ type: 'success', message: 'User updated successfully.' });
      closeEditUserModal();
    } catch (error) {
      const message = error?.message || 'Failed to update user. Please try again.';
      setEditUserErrors((prev) => ({ ...prev, submit: message }));
    } finally {
      setIsSavingUser(false);
    }
  };

  const openDeleteUserModal = (user) => {
    setDeleteUserTarget(user);
  };

  const closeDeleteUserModal = () => {
    setDeleteUserTarget(null);
  };

  const handleDeleteUser = async () => {
    if (!deleteUserTarget?.id) return;
    if (deleteUserTarget?.id === userProfile?.id) {
      setBanner({
        type: 'error',
        message: 'You cannot delete your own account.',
      });
      return;
    }

    setIsDeletingUser(true);
    try {
      await userService.deleteUser(deleteUserTarget.id);
      setUsers((prev) => prev.filter((user) => user?.id !== deleteUserTarget.id));
      setBanner({
        type: 'success',
        message: `User ${deleteUserTarget?.fullName || ''} deleted successfully.`,
      });
      closeDeleteUserModal();
    } catch (error) {
      setBanner({
        type: 'error',
        message: error?.message || 'Failed to delete user.',
      });
    } finally {
      setIsDeletingUser(false);
    }
  };

  const handleFilterSubmit = (event) => {
    event?.preventDefault();
    setActiveFilters({
      searchTerm: filtersDraft?.searchTerm?.trim(),
      role: filtersDraft?.role || 'all',
    });
  };

  const handleClearFilters = () => {
    setFiltersDraft(initialFilters);
    setActiveFilters(initialFilters);
  };

  const isUserUpdating = (userId) => updatingUserIds?.has(userId);
  const isUserResetting = (userId) => resettingUserIds?.has(userId);

  const filteredBannerClassName = useMemo(() => {
    if (!banner) return '';
    if (banner?.type === 'success') return 'bg-success/10 border-success/20 text-success';
    if (banner?.type === 'error') return 'bg-destructive/10 border-destructive/20 text-destructive';
    return 'bg-muted border-border text-foreground';
  }, [banner]);

  const formatDate = (value) => {
    if (!value) return '-';
    try {
      const date = new Date(value);
      if (Number.isNaN(date?.getTime())) return '-';
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })?.format(date);
    } catch {
      return '-';
    }
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="text-primary mx-auto mb-4 animate-spin" />
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show access denied for non-admin users
  if (!canManageUsers) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Icon name="ShieldAlert" size={48} className="text-warning mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-foreground mb-2">Access Denied</h2>
            <p className="text-muted-foreground">You don't have permission to access this page.</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/admin-dashboard')}
            >
              Go to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex">
          <Sidebar isCollapsed={isSidebarCollapsed} onToggle={handleSidebarToggle} />
          <main className="flex-1 ml-72 mt-16 p-6">
          <div className="max-w-6xl mx-auto space-y-6">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">User Management</h1>
                <p className="text-muted-foreground mt-1">
                  Invite administrators, team managers, and staff directly from the system.
                </p>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Shield" size={16} />
                <span>Service key required for admin invitations</span>
              </div>
            </div>

            {banner && (
              <div className={`px-4 py-3 rounded-lg border ${filteredBannerClassName}`}>
                <div className="flex items-start gap-3">
                  <Icon
                    name={
                      banner?.type === 'success'
                        ? 'CheckCircle2'
                        : banner?.type === 'error'
                        ? 'AlertTriangle'
                        : 'Info'
                    }
                    size={18}
                  />
                  <div className="space-y-1">
                    <p className="font-medium">{banner?.message}</p>
                    {banner?.type === 'error' && banner?.message?.includes('Admin client') && (
                      <p className="text-sm text-muted-foreground">
                        Ensure the API server is running and `SUPABASE_SERVICE_ROLE_KEY` is configured in `api/.env`.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <div className="bg-card border border-border rounded-lg p-6 space-y-6 card-shadow">
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Invite New User</h2>
                    <p className="text-sm text-muted-foreground">
                      Create user accounts directly without requiring self-registration.
                    </p>
                  </div>
                  <form className="space-y-4" onSubmit={handleCreateUser}>
                    <Input
                      label="Full Name"
                      required
                      placeholder="Jane Doe"
                      value={formState?.fullName}
                      onChange={(event) => handleFormChange('fullName', event?.target?.value)}
                      error={formErrors?.fullName}
                    />
                    <Input
                      label="Email Address"
                      type="email"
                      required
                      placeholder="jane.doe@example.com"
                      value={formState?.email}
                      onChange={(event) => handleFormChange('email', event?.target?.value)}
                      error={formErrors?.email}
                    />
                    <Input
                      label="Phone Number"
                      type="tel"
                      placeholder="+255 700 000 000"
                      value={formState?.phone}
                      onChange={(event) => handleFormChange('phone', event?.target?.value)}
                      error={formErrors?.phone}
                    />
                    <Select
                      label="Role"
                      required
                      placeholder="Select user role"
                      options={roleOptions}
                      value={formState?.role}
                      onChange={(value) => {
                        setFormState((prev) => ({
                          ...prev,
                          role: value,
                          extraPermissions: [],
                          region: value !== 'region_admin' ? '' : prev.region,
                        }));
                        setFormErrors((prev) => ({
                          ...prev,
                          role: undefined,
                          region: undefined,
                        }));
                      }}
                      error={formErrors?.role}
                      searchable
                    />
                    {formState?.role === 'region_admin' && (
                      <Select
                        label="Assigned Region"
                        required
                        placeholder="Select region"
                        options={TANZANIA_REGION_OPTIONS}
                        value={formState?.region}
                        onChange={(value) => handleFormChange('region', value)}
                        error={formErrors?.region}
                        description="Region admin can only manage this region association, teams and players."
                        searchable
                      />
                    )}
                    {formState?.role && (
                      <div className="rounded-lg border border-border p-4 bg-muted/20">
                        <RoleAccessSummary
                          role={formState.role}
                          extraPermissions={formState.extraPermissions}
                          onExtraPermissionsChange={(next) =>
                            handleFormChange('extraPermissions', next)
                          }
                          editable
                        />
                      </div>
                    )}
                    <Input
                      label="Password"
                      type="password"
                      required
                      placeholder="Enter a secure password"
                      value={formState?.password}
                      onChange={(event) => handleFormChange('password', event?.target?.value)}
                      error={formErrors?.password}
                    />
                    <Input
                      label="Confirm Password"
                      type="password"
                      required
                      placeholder="Retype the password"
                      value={formState?.confirmPassword}
                      onChange={(event) =>
                        handleFormChange('confirmPassword', event?.target?.value)
                      }
                      error={formErrors?.confirmPassword}
                    />
                    <Button
                      type="submit"
                      className="w-full"
                      iconName="UserPlus"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      Invite User
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full"
                      onClick={resetForm}
                      disabled={isSubmitting}
                    >
                      Reset Form
                    </Button>
                  </form>

                  <div className="rounded-lg bg-muted/40 px-4 py-3 text-sm text-muted-foreground space-y-2">
                    <div className="flex items-start gap-2">
                      <Icon name="Info" size={16} className="mt-0.5" />
                      <div>
                        <p className="font-medium text-foreground">User Creation</p>
                        <p>
                          User creation is handled securely via the backend API. Ensure the API server is running and configured with `SUPABASE_SERVICE_ROLE_KEY` in `api/.env`.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2 space-y-6">
                <div className="bg-card border border-border rounded-lg p-6 card-shadow">
                  <form
                    className="flex flex-col md:flex-row md:items-end gap-4"
                    onSubmit={handleFilterSubmit}
                  >
                    <div className="flex-1">
                      <Input
                        label="Search Users"
                        placeholder="Search by name, email, or phone"
                        value={filtersDraft?.searchTerm}
                        onChange={(event) =>
                          setFiltersDraft((prev) => ({
                            ...prev,
                            searchTerm: event?.target?.value,
                          }))
                        }
                      />
                    </div>
                    <div className="w-full md:w-56">
                      <Select
                        label="Role"
                        value={filtersDraft?.role}
                        onChange={(value) =>
                          setFiltersDraft((prev) => ({
                            ...prev,
                            role: value || 'all',
                          }))
                        }
                        options={[
                          { label: 'All roles', value: 'all' },
                          ...roleOptions,
                        ]}
                        clearable
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button type="submit" iconName="Search">
                        Apply
                      </Button>
                      <Button type="button" variant="outline" onClick={handleClearFilters}>
                        Reset
                      </Button>
                    </div>
                  </form>
                </div>

                <div className="bg-card border border-border rounded-lg card-shadow">
                  <div className="p-6 border-b border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-semibold text-foreground">Directory</h2>
                      <p className="text-sm text-muted-foreground">
                        {isLoadingUsers
                          ? 'Loading users...'
                          : `${users?.length ?? 0} user${users?.length === 1 ? '' : 's'} found`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Icon name="UserCheck" size={16} />
                      <span>Active invitations sync automatically</span>
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-border">
                      <thead>
                        <tr className="bg-muted/50 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          <th className="px-6 py-3">User</th>
                          <th className="px-6 py-3">Role</th>
                          <th className="px-6 py-3">Status</th>
                          <th className="px-6 py-3">Created</th>
                          <th className="px-6 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {isLoadingUsers ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-10 text-center text-muted-foreground">
                              <div className="flex items-center justify-center gap-2">
                                <svg
                                  className="animate-spin h-5 w-5 text-muted-foreground"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                    fill="none"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  />
                                </svg>
                                <span>Fetching the latest user directory…</span>
                              </div>
                            </td>
                          </tr>
                        ) : users?.length === 0 ? (
                          <tr>
                            <td colSpan="5" className="px-6 py-12 text-center text-muted-foreground">
                              <div className="flex flex-col items-center gap-3">
                                <Icon name="Users" size={24} />
                                <p className="font-medium text-foreground">
                                  No users found for the selected filters
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  Try adjusting your filters or invite a new user.
                                </p>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          users?.map((user) => (
                            <tr key={user?.id} className="hover:bg-muted/40 transition-colors">
                              <td className="px-6 py-4">
                                <div className="flex flex-col">
                                  <span className="font-medium text-foreground">
                                    {user?.fullName || 'Unnamed User'}
                                  </span>
                                  <span className="text-sm text-muted-foreground flex items-center gap-2">
                                    <Icon name="Mail" size={14} />
                                    {user?.email || 'No email'}
                                  </span>
                                  {user?.phone && (
                                    <span className="text-sm text-muted-foreground flex items-center gap-2">
                                      <Icon name="Phone" size={14} />
                                      {user?.phone}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <span className="inline-flex flex-col items-start gap-0.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                                  <span className="inline-flex items-center gap-2">
                                    <Icon name="IdCard" size={14} />
                                    {getRoleLabel(user?.role)}
                                  </span>
                                  {user?.role === 'region_admin' && user?.region && (
                                    <span className="text-xs text-muted-foreground pl-5">
                                      {getRegionLabel(user.region)}
                                    </span>
                                  )}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                <span
                                  className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${
                                    user?.isActive
                                      ? 'bg-success/10 text-success'
                                      : 'bg-destructive/10 text-destructive'
                                  }`}
                                >
                                  <span className="w-2 h-2 rounded-full bg-current" />
                                  {user?.isActive ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-muted-foreground">
                                {formatDate(user?.createdAt)}
                              </td>
                              <td className="px-6 py-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => openEditUserModal(user)}
                                    title="Edit user"
                                  >
                                    <Icon name="Edit" size={16} />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    iconName="KeyRound"
                                    onClick={() => handleSendPasswordReset(user)}
                                    title="Send password reset email"
                                    loading={isUserResetting(user?.id)}
                                    disabled={!user?.email || isUserResetting(user?.id)}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-destructive hover:text-destructive"
                                    onClick={() => openDeleteUserModal(user)}
                                    title={
                                      user?.id === userProfile?.id
                                        ? 'You cannot delete your own account'
                                        : 'Delete user'
                                    }
                                    disabled={user?.id === userProfile?.id}
                                  >
                                    <Icon name="Trash2" size={16} />
                                  </Button>
                                  <Button
                                    variant={user?.isActive ? 'outline' : 'success'}
                                    size="sm"
                                    iconName={user?.isActive ? 'UserX' : 'UserCheck'}
                                    onClick={() => handleToggleUserStatus(user)}
                                    loading={isUserUpdating(user?.id)}
                                    disabled={isUserUpdating(user?.id)}
                                  >
                                    {user?.isActive ? 'Deactivate' : 'Activate'}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {usersError && !isLoadingUsers && (
                    <div className="border-t border-border px-6 py-4 bg-destructive/10 text-destructive text-sm">
                      {usersError}
                    </div>
                  )}
                </div>
              </div>
            </section>
          </div>
          </main>
        </div>
      </div>

      {/* Edit User Modal */}
      {editUserModalOpen && userBeingEdited && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-lg w-full card-shadow">
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <h3 className="text-lg font-semibold text-foreground">Edit User</h3>
                <p className="text-sm text-muted-foreground">Update user details and access level</p>
              </div>
              <Button variant="ghost" size="icon" onClick={closeEditUserModal}>
                <Icon name="X" size={18} />
              </Button>
            </div>
            <form onSubmit={handleUpdateUserProfile} className="p-6 space-y-4">
              <Input
                label="Full Name"
                value={editUserForm?.fullName}
                onChange={(event) => handleEditUserChange('fullName', event?.target?.value)}
                error={editUserErrors?.fullName}
                required
              />
              <Input
                label="Email Address"
                value={userBeingEdited?.email || ''}
                readOnly
                description="Email cannot be changed"
              />
              <Input
                label="Phone Number"
                value={editUserForm?.phone || ''}
                onChange={(event) => handleEditUserChange('phone', event?.target?.value)}
              />
              <Select
                label="Role"
                value={editUserForm?.role}
                onChange={(value) => {
                  setEditUserForm((prev) => ({
                    ...prev,
                    role: value,
                    extraPermissions: [],
                    region: value !== 'region_admin' ? '' : prev.region,
                  }));
                  if (editUserErrors?.role) {
                    setEditUserErrors((prev) => ({ ...prev, role: undefined, region: undefined }));
                  }
                }}
                options={roleOptions}
                error={editUserErrors?.role}
              />
              {editUserForm?.role === 'region_admin' && (
                <Select
                  label="Assigned Region"
                  required
                  placeholder="Select region"
                  options={TANZANIA_REGION_OPTIONS}
                  value={editUserForm?.region}
                  onChange={(value) => handleEditUserChange('region', value)}
                  error={editUserErrors?.region}
                  searchable
                />
              )}
              {editUserForm?.role && (
                <RoleAccessSummary
                  role={editUserForm.role}
                  extraPermissions={editUserForm.extraPermissions}
                  onExtraPermissionsChange={(next) =>
                    handleEditUserChange('extraPermissions', next)
                  }
                  editable
                />
              )}
              {editUserErrors?.submit && (
                <p className="text-sm text-destructive">{editUserErrors?.submit}</p>
              )}
              <div className="flex items-center justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={closeEditUserModal} disabled={isSavingUser}>
                  Cancel
                </Button>
                <Button type="submit" iconName="Save" loading={isSavingUser} disabled={isSavingUser}>
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Modal */}
      {deleteUserTarget && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full card-shadow">
            <div className="p-6 text-center space-y-4">
              <Icon name="AlertTriangle" size={48} className="text-destructive mx-auto" />
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-2">Delete User</h3>
                <p className="text-muted-foreground">
                  Are you sure you want to permanently delete{' '}
                  <span className="font-medium text-foreground">
                    {deleteUserTarget?.fullName || deleteUserTarget?.email}
                  </span>
                  ? This action cannot be undone.
                </p>
                {deleteUserTarget?.id === userProfile?.id && (
                  <p className="text-sm text-destructive mt-2">
                    You cannot delete your own account while logged in.
                  </p>
                )}
              </div>
              <div className="flex items-center justify-center gap-3">
                <Button variant="outline" onClick={closeDeleteUserModal} disabled={isDeletingUser}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDeleteUser}
                  loading={isDeletingUser}
                  disabled={isDeletingUser || deleteUserTarget?.id === userProfile?.id}
                >
                  Delete User
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserManagementPage;


